import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Radio,
  FolderKanban,
  Settings,
  ShieldCheck,
  LogOut,
  Users,
  Infinity as InfinityIcon,
  Building2,
  AlertTriangle,
  Users2,
  TrendingUp,
  Sparkles,
  Menu,
  X,
  Gavel,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import AskAiWidget from "./AskAiWidget";

export default function AppShell({ children }) {
  const { user, org, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(() =>
    location.pathname.startsWith("/app/admin"),
  );
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("lr_sidebar_collapsed") === "1",
  );

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem("lr_sidebar_collapsed", next ? "1" : "0");
      return next;
    });
  };

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMobileOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  // Auto-expand the ORL Admin accordion when landing directly on one of its routes.
  useEffect(() => {
    if (location.pathname.startsWith("/app/admin")) {
      setAdminOpen(true);
    }
  }, [location.pathname]);

  const navItems = [
    {
      to: "/app",
      label: "Dashboard",
      icon: LayoutDashboard,
      end: true,
      testid: "nav-dashboard",
    },
    {
      to: "/app/feed",
      label: "Intelligence Feed",
      icon: Radio,
      testid: "nav-feed",
    },
    {
      to: "/app/tenders",
      label: "Tenders",
      icon: Gavel,
      testid: "nav-tenders",
    },
    {
      to: "/app/projects",
      label: "Projects",
      icon: FolderKanban,
      testid: "nav-projects",
    },
    {
      to: "/app/trending",
      label: "Trends",
      icon: TrendingUp,
      testid: "nav-trending",
    },
    {
      to: "/app/ai-analysis",
      label: "Analysis",
      icon: Sparkles,
      testid: "nav-trending",
    },
    {
      to: "/app/organisation",
      label: "About Organisation",
      icon: Building2,
      testid: "nav-organisation",
    },
    // { to: "/app/action-items", label: "Action Items", icon: AlertTriangle, testid: "nav-action-items" },
    { to: "/app/team", label: "Team", icon: Users, testid: "nav-team" },
    // P1#3 — credits removed from the customer experience; "Usage" (credit balance) hidden.
    // { to: "/app/usage", label: "Usage", icon: InfinityIcon, testid: "nav-usage" },
    {
      to: "/app/settings",
      label: "Settings",
      icon: Settings,
      testid: "nav-settings",
    },
  ];
  if (user?.role === "orl_admin") {
    navItems.push({
      to: "/app/admin",
      label: "ORL Admin",
      icon: ShieldCheck,
      testid: "nav-admin",
      children: [
        {
          to: "/app/admin/client-projects",
          label: "Clients",
          icon: Building2,
          testid: "nav-clients",
        },
        {
          to: "/app/admin/cost-monitor",
          label: "Cost Monitor",
          icon: InfinityIcon,
          testid: "nav-cost-monitor",
        },
        {
          to: "/app/admin/waitlist",
          label: "Waitlist",
          icon: Users2,
          testid: "nav-waitlist",
        },
      ],
    });
  }

  const renderSidebar = (isCollapsed, isMobile = false) => (
    <>
      <Link
        to="/app"
        className={`flex items-center justify-center border-b border-[#E2E8F0] overflow-hidden ${
          isCollapsed ? "h-16 px-2" : "h-auto py-4 px-6 flex-col gap-1"
        }`}
      >
        {isCollapsed ? (
          <img
            src="/launchRadarLogo.png"
            alt="LaunchRadar"
            className="h-8 w-8 object-contain"
          />
        ) : (
          <>
            <img
              src="/logo.png"
              alt="LaunchRadar Logo"
              className="w-[150px] max-w-full h-auto"
            />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] leading-tight whitespace-nowrap">
                Intelligence OS
              </div>
            </div>
          </>
        )}
      </Link>
      <nav className="flex-1 p-3 space-y-1  !scrollbar-none  !overflow-auto overflow-x-hidden">
        {navItems.map((it) => {
          // Items with children (currently only "ORL Admin") render as an
          // accordion group instead of a plain link, in BOTH collapsed and
          // expanded states — clicking the header always navigates to its
          // overview page and toggles the submenu.
          if (it.children && it.children.length > 0) {
            const isAdminSectionActive =
              location.pathname.startsWith("/app/admin");

            if (isCollapsed) {
              return (
                <div key={it.testid} className="space-y-1">
                  <button
                    type="button"
                    onClick={() => {
                      navigate(it.to);
                      setAdminOpen((o) => !o);
                    }}
                    aria-expanded={adminOpen}
                    aria-controls="sidebar-admin-submenu-collapsed"
                    data-testid={it.testid}
                    title={it.label}
                    className={`w-full flex items-center justify-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors min-h-[44px] ${
                      isAdminSectionActive
                        ? "bg-[#0F1F3D] text-white"
                        : "text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F1F3D]"
                    }`}
                  >
                    <it.icon className="h-5 w-5 shrink-0" />
                  </button>

                  {adminOpen && (
                    <div
                      id="sidebar-admin-submenu-collapsed"
                      className="space-y-1"
                    >
                      {it.children.map((child) => (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          data-testid={child.testid}
                          title={child.label}
                          className={({ isActive }) =>
                            `flex items-center justify-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors min-h-[44px] ${
                              isActive
                                ? "bg-[#0F1F3D] text-white"
                                : "text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F1F3D]"
                            }`
                          }
                        >
                          <child.icon className="h-5 w-5 shrink-0" />
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <div key={it.testid}>
                <button
                  type="button"
                  onClick={() => {
                    navigate(it.to);
                    setAdminOpen((o) => !o);
                  }}
                  aria-expanded={adminOpen}
                  aria-controls="sidebar-admin-submenu"
                  data-testid={it.testid}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors min-h-[44px] ${
                    isAdminSectionActive
                      ? "bg-[#0F1F3D] text-white"
                      : "text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F1F3D]"
                  }`}
                >
                  <it.icon className="h-5 w-5 shrink-0" />
                  <span className="flex-1 text-left">{it.label}</span>
                  {adminOpen ? (
                    <ChevronUp className="h-4 w-4 shrink-0" />
                  ) : (
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  )}
                </button>

                {adminOpen && (
                  <div
                    id="sidebar-admin-submenu"
                    className="mt-1 ml-4 pl-3 border-l border-[#E2E8F0] space-y-1"
                  >
                    {it.children.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        data-testid={child.testid}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors min-h-[40px] ${
                            isActive
                              ? "bg-[#0F1F3D] text-white"
                              : "text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F1F3D]"
                          }`
                        }
                      >
                        <child.icon className="h-4 w-4 shrink-0" />
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          return (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={it.testid}
              title={isCollapsed ? it.label : undefined}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors min-h-[44px] ${isCollapsed ? "justify-center" : ""} ${
                  isActive
                    ? "bg-[#0F1F3D] text-white"
                    : "text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F1F3D]"
                }`
              }
            >
              <it.icon className="h-5 w-5 shrink-0" />
              {!isCollapsed && it.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="relative p-3 border-t border-[#E2E8F0] shrink-0">
        {isCollapsed ? (
          <div className="relative flex flex-col items-center">
            <button
              type="button"
              onClick={() => setProfileOpen((open) => !open)}
              aria-expanded={profileOpen}
              aria-controls="sidebar-profile-details"
              aria-label="Open profile"
              data-testid="profile-toggle"
              className="h-10 w-10 rounded-full bg-[#D1FAE5] text-[#065F46] flex items-center justify-center text-sm font-semibold hover:ring-2 hover:ring-[#A7F3D0] transition-all"
            >
              {(org?.name || "O")
                .trim()
                .split(/\s+/)
                .filter(Boolean)
                .map((part) => part[0])
                .slice(0, 2)
                .join("")
                .toUpperCase()}
            </button>

            {profileOpen && (
              <div
                id="sidebar-profile-details"
                data-testid="profile-details"
                className="absolute left-[calc(100%+8px)] bottom-0 z-[60] w-[300px] rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-xl"
              >
                <span className="absolute left-[-7px] bottom-4 h-3.5 w-3.5 rotate-45 border-l border-b border-[#E2E8F0] bg-white" />
                <div className="relative flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="type-body-sm text-[#0F1F3D] font-semibold">
                      {org?.name || "-"}
                    </div>
                    <div className="type-caption text-[#64748B] mt-0.5">
                      {user?.full_name || "-"}
                    </div>
                    <div className="type-caption text-[#64748B] mt-1 break-all">
                      {user?.email || "-"}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setProfileOpen(false);
                        navigate("/app/settings");
                      }}
                      className="mt-3 type-body-sm text-[#2563EB] font-medium hover:underline"
                      data-testid="view-profile-button"
                    >
                      View profile →
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="relative">
            <button
              type="button"
              onClick={() => setProfileOpen((open) => !open)}
              aria-expanded={profileOpen}
              aria-controls="sidebar-profile-details"
              data-testid="profile-toggle"
              className="w-full flex items-center gap-3 px-2 py-2 rounded-md text-left hover:bg-[#F8FAFC] transition-colors"
            >
              <div className="h-10 w-10 shrink-0 rounded-full bg-[#D1FAE5] text-[#065F46] flex items-center justify-center text-sm font-semibold">
                {(org?.name || "O")
                  .trim()
                  .split(/\s+/)
                  .filter(Boolean)
                  .map((part) => part[0])
                  .slice(0, 2)
                  .join("")
                  .toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div
                  className="type-body-sm text-[#0F1F3D] font-semibold truncate"
                  data-testid="org-name-label"
                >
                  {org?.name || "-"}
                </div>
                <div
                  className="type-caption text-[#64748B] mt-0.5 truncate"
                  data-testid="user-name-label"
                >
                  {user?.full_name || "-"}
                </div>
              </div>
              {profileOpen ? (
                <ChevronUp className="h-4 w-4 shrink-0 text-[#0F1F3D]" />
              ) : (
                <ChevronDown className="h-4 w-4 shrink-0 text-[#0F1F3D]" />
              )}
            </button>

            {profileOpen && (
              <div
                id="sidebar-profile-details"
                data-testid="profile-details"
                className={`absolute z-[60] rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-xl ${
                  isMobile
                    ? "left-2 right-2 bottom-[calc(100%+8px)]"
                    : "left-0 right-0 bottom-[calc(100%+8px)]"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="type-body-sm text-[#0F1F3D] font-semibold truncate">
                      {org?.name || "-"}
                    </div>
                    <div className="type-caption text-[#64748B] mt-0.5">
                      {user?.full_name || "-"}
                    </div>
                    <div className="type-caption text-[#64748B] mt-1 break-all">
                      {user?.email || "-"}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setProfileOpen(false);
                        navigate("/app/settings");
                      }}
                      className="mt-3 type-body-sm text-[#2563EB] font-medium hover:underline"
                      data-testid="view-profile-button"
                    >
                      View Profile →
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <button
          onClick={async () => {
            try {
              await api.post("/auth/logout");
            } catch (e) {
              /* ignore */
            }
            logout();
            navigate("/");
          }}
          data-testid="logout-button"
          title={isCollapsed ? "Sign out" : undefined}
          className={`w-full ${isCollapsed ? "mt-3" : "mt-2"} flex items-center justify-center gap-2 px-3 py-2 rounded-md type-body-sm border border-[#E2E8F0] hover:bg-[#F1F5F9] min-h-[44px]`}
        >
          <LogOut className="h-4 w-4" /> {!isCollapsed && "Sign out"}
        </button>
      </div>
    </>
  );

  const bottomItems = navItems.slice(0, 4);

  return (
    <div className="h-screen scroll flex bg-[#F8F9FA] overflow-hidden">
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex ${collapsed ? "w-20" : "w-64"} shrink-0 border-r border-[#E2E8F0] bg-white flex-col h-screen overflow-visible relative transition-[width] duration-200`}
        data-testid="app-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
      >
        {renderSidebar(collapsed)}
        <button
          onClick={toggleCollapsed}
          data-testid="sidebar-collapse-toggle"
          aria-label={collapsed ? "Expand menu" : "Collapse menu"}
          title={collapsed ? "Expand menu" : "Collapse menu"}
          className="absolute top-5 -right-3 z-20 h-6 w-6 flex items-center justify-center rounded-full border border-[#E2E8F0] bg-white text-[#475569] shadow-sm hover:bg-[#F1F5F9] hover:text-[#0F1F3D]"
        >
          {collapsed ? (
            <PanelLeftOpen className="h-3.5 w-3.5" />
          ) : (
            <PanelLeftClose className="h-3.5 w-3.5" />
          )}
        </button>
      </aside>

      {/* Mobile slide-over drawer */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40"
          data-testid="mobile-drawer"
        >
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute left-0 top-0 h-full w-72 max-w-[85%] min-w-0 overflow-hidden bg-white flex flex-col shadow-xl">
            <button
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-drawer-close"
              className="absolute right-3 top-3 z-10 h-9 w-9 flex items-center justify-center rounded-md hover:bg-[#F1F5F9]"
            >
              <X className="h-5 w-5 text-[#64748B]" />
            </button>
            {renderSidebar(false, true)}
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col h-screen">
        {/* Mobile top bar */}
        <header className="lg:hidden h-14 shrink-0 flex items-center justify-between px-4 border-b border-[#E2E8F0] bg-white">
          <button
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-button"
            className="h-10 w-10 flex items-center justify-center rounded-md hover:bg-[#F1F5F9] -ml-2"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5 text-[#0F1F3D]" />
          </button>
          <Link to="/app">
            <img src="/logo.png" alt="LaunchRadar" className="h-6 w-auto" />
          </Link>
          <div className="w-10" />
        </header>

        <main className="flex-1 min-w-0 overflow-y-auto pb-20 lg:pb-0">
          {children}
        </main>

        <AskAiWidget />

        {/* Mobile bottom nav */}
        <nav
          className="lg:hidden fixed bottom-0 inset-x-0 z-30 h-16 bg-white border-t border-[#E2E8F0] flex items-stretch"
          data-testid="mobile-bottom-nav"
        >
          {bottomItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`bottom-${it.testid}`}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium min-h-[44px] ${isActive ? "text-[#0F1F3D]" : "text-[#94A3B8]"}`
              }
            >
              <it.icon className="h-5 w-5" />
              <span className="truncate max-w-full px-1">
                {it.label.split(" ")[0]}
              </span>
            </NavLink>
          ))}
          <button
            onClick={() => setMobileOpen(true)}
            data-testid="bottom-more"
            className="flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium text-[#94A3B8] min-h-[44px]"
          >
            <Menu className="h-5 w-5" />
            <span>More</span>
          </button>
        </nav>
      </div>
    </div>
  );
}
